import { config } from '../constants'
import { Storage, IStorageConfig } from './storage'
import { Locale } from './locale'
import { ConsoleLog } from './logging'
const DEBUG_ROUTING: boolean = true

export class Routing {
  public static JoinURLParts(...parts: string[]): string {
    return parts.map(part => part.replace(/^\/+|\/+$/g, '')).join('/')
  }

  public static GetLastSlashTrimmedURL(url: string): string {
    return url.replace(/\/$/, '')
  }
  public static SKIP_PROFILE_COMPLETION: string[] = config.skipProfileCompletion
  public static HOME_PAGE_KEY: string = config.routingHomePageKey
  public static ROUTES: any = config.routing
  public static HOST: string = config.routingHost
  public static SKIP_ROUTE_LOGGING: string[] = config.skipRouteLogging
  public static ROUTE_GUARD: string[] = config.routeGuard
  public static PROSPACE_ROUTE_GUARD: string[] = config.prospaceRouteGuard
  public static IGNORE_ROUTE_GUARD: string[] = config.ignoreRouteGuard
  public static ROUTE_PROCEED_AFTER_SESSION: string[] = config.routeProceedAfterSession

  _storage: Storage = new Storage({ useSession: true } as IStorageConfig)
  _lang: string = Locale.getInstance().getLanguage()
  _isDirty: boolean = false

  constructor() {
    this.setLastPage()
    window.addEventListener('beforeunload', this.beforeUnloadCallback.bind(this))
  }

  public setDirty(): void {
    this._isDirty = true
  }

  public clearDirty(): void {
    this._isDirty = false
  }

  public beforeUnloadCallback(event: BeforeUnloadEvent): string | void {
    if (this._isDirty) {
      const message = Locale.getKey('general.unsavedChanges')
      event.returnValue = message // Standard for most browsers
      return message // For some older browsers
    }
  }

  public getCurrentPage(): string {
    ConsoleLog(DEBUG_ROUTING, 'getCurrentPage', 'enter')
    let hostParts = window?.location?.href?.split('?') || ['']
    ConsoleLog(DEBUG_ROUTING, 'getCurrentPage', 'hostParts', hostParts)
    return hostParts[0]
  }

  public currentPageHasQueryString(): boolean {
    ConsoleLog(DEBUG_ROUTING, 'currentPageHasQueryString', 'enter')
    let hostParts = window?.location?.href?.split('?') || ['']
    return hostParts.length > 1
  }

  public setLanguage(lang: string): void {
    this._lang = lang
  }
  public getLanguage(): string {
    return this._lang
  }

  private getLastPage(): string {
    ConsoleLog(DEBUG_ROUTING, 'getLastPage', 'enter')
    let lastPage = this._storage.getItem('lastPage') ?? Routing.HOST
    ConsoleLog(DEBUG_ROUTING, 'getLastPage', 'lastPage', lastPage)
    return lastPage
  }
  private setLastPage(): void {
    ConsoleLog(DEBUG_ROUTING, 'setLastPage', 'enter')
    let location: string = this.getCurrentPage()
    let ignoreable: string[] = this.getLocationListValuesFromRoutes(Routing.SKIP_ROUTE_LOGGING)
    let isIgnored: boolean = this.isStringInList(location, ignoreable)
    let url404: string = Routing.JoinURLParts(Routing.HOST, '404')
    let is404: boolean = location.indexOf(url404) > -1

    if (!isIgnored && !is404) {
      ConsoleLog(
        DEBUG_ROUTING,
        'setLastPage',
        'location',
        location,
        'isIgnored',
        isIgnored,
        'is404',
        is404
      )
      this._storage.setItem('lastPage', window.location.href)
    }
  }
  private removeLastPage(): void {
    ConsoleLog(DEBUG_ROUTING, 'removeLastPage', 'enter')
    this._storage.removeItem('lastPage')
  }

  public goToLastPage(forceHome?: boolean): void {
    ConsoleLog(DEBUG_ROUTING, 'goToLastPage', 'enter', forceHome)
    let lastPage = this.getLastPage()
    this.removeLastPage()

    let hostPage = this.getRoute(Routing.HOME_PAGE_KEY)
    let lastPageKey = this.getRouteKeyFromLocation(lastPage, 'en')
    if (!lastPageKey) {
      ConsoleLog(DEBUG_ROUTING, 'goToLastPage', 'no en key fetching fr', lastPage)
      lastPageKey = this.getRouteKeyFromLocation(lastPage, 'fr')
    }

    if (forceHome) {
      ConsoleLog(DEBUG_ROUTING, 'goToLastPage', 'force home', hostPage)
      lastPage = hostPage
    }

    if (lastPage == this.getCurrentPage()) {
      ConsoleLog(DEBUG_ROUTING, 'goToLastPage', 'same page', hostPage)
      this.goTo(hostPage)
      return
    }

    if (lastPageKey) {
      let route = this.getRoute(lastPageKey)
      ConsoleLog(DEBUG_ROUTING, 'goToLastPage', 'route', route)
      this.goTo(route)
      return
    }

    ConsoleLog(DEBUG_ROUTING, 'goToLastPage', 'last page', lastPage)
    this.goTo(lastPage)
  }

  public goTo(location: string, force?: boolean): void {
    ConsoleLog(DEBUG_ROUTING, 'goTo', 'enter', location, force)
    let currentPage = this.getCurrentPage()

    if (force) {
      ConsoleLog(DEBUG_ROUTING, 'goTo', 'force', location)
      window.location.href = location
    }

    if (this.currentPageHasQueryString()) {
      ConsoleLog(DEBUG_ROUTING, 'goTo', 'has query string', location)
      if (location.indexOf(currentPage) == -1) {
        ConsoleLog(DEBUG_ROUTING, 'goTo', 'go to', location)
        window.location.href = location
      } else {
        ConsoleLog(DEBUG_ROUTING, 'goTo', 'replace state', location)
        window.history.replaceState({}, '', location)
      }
    } else {
      if (location == currentPage) {
        ConsoleLog(DEBUG_ROUTING, 'goTo', 'stay on the page', location)
        return
      }

      ConsoleLog(DEBUG_ROUTING, 'goTo', 'go to', location)
      window.location.href = location
    }
  }

  public proceedAfterSession(): void {
    ConsoleLog(DEBUG_ROUTING, 'proceedAfterSession', 'enter')
    const currentPage = this.getCurrentPage()
    const currentPageKey = this.getRouteKeyFromLocation(currentPage)
    const lastPage = this.getLastPage()
    const lastPageKey = this.getRouteKeyFromLocation(lastPage)
    let isProceedAfterSession = this.isStringInList(
      currentPageKey,
      Routing.ROUTE_PROCEED_AFTER_SESSION
    )
    let isLastProceedAfterSession = this.isStringInList(
      lastPageKey,
      Routing.ROUTE_PROCEED_AFTER_SESSION
    )

    if (isProceedAfterSession && isLastProceedAfterSession) {
      ConsoleLog(
        DEBUG_ROUTING,
        'proceedAfterSession',
        'both current and last page are proceed after session',
        currentPage,
        lastPage
      )
      this.goTo(Routing.HOST)
    } else if (isProceedAfterSession && !isLastProceedAfterSession) {
      ConsoleLog(
        DEBUG_ROUTING,
        'proceedAfterSession',
        'current page is proceed after session',
        currentPage,
        lastPage
      )
      this.goToLastPage()
    } else if (!isProceedAfterSession && isLastProceedAfterSession) {
      ConsoleLog(
        DEBUG_ROUTING,
        'proceedAfterSession',
        'current page is not proceed after session, do nothing',
        currentPage,
        lastPage
      )
    } else {
      ConsoleLog(
        DEBUG_ROUTING,
        'proceedAfterSession',
        'neither current nor last page is proceed after session, do nothing',
        currentPage,
        lastPage
      )
    }
  }

  public guardCurrentProspaceRoute(isLoggedIn: boolean, isProspace: boolean): void {
    ConsoleLog(DEBUG_ROUTING, 'guardCurrentProspaceRoute', 'enter', isLoggedIn)
    let currentPage = this.getCurrentPage()
    let currentPageKey = this.getRouteKeyFromLocation(currentPage)
    let isGuardedRoute = this.isStringInList(currentPageKey, Routing.PROSPACE_ROUTE_GUARD)

    ConsoleLog(
      DEBUG_ROUTING,
      'prospace_route_guard',
      Routing.PROSPACE_ROUTE_GUARD,
      this.getLocationListValuesFromRoutes(Routing.PROSPACE_ROUTE_GUARD),
      this.getCurrentPage()
    )

    if (!isGuardedRoute || !isLoggedIn) {
      ConsoleLog(
        DEBUG_ROUTING,
        'guardCurrentProspaceRoute',
        'routing through default guard',
        isGuardedRoute,
        isLoggedIn,
        isProspace
      )
      return this.guardCurrentRoute(isLoggedIn)
    } else {
      if (!isProspace) {
        ConsoleLog(
          DEBUG_ROUTING,
          'guardCurrentProspaceRoute',
          'not prospace',
          isGuardedRoute,
          isLoggedIn,
          isProspace
        )
        this.goTo(Routing.HOST)
      } else {
        ConsoleLog(
          DEBUG_ROUTING,
          'guardCurrentProspaceRoute',
          'is prospace',
          isGuardedRoute,
          isLoggedIn,
          isProspace
        )
      }
    }
  }

  public guardCurrentRoute(isLoggedIn: boolean): void {
    ConsoleLog(DEBUG_ROUTING, 'guardCurrentRoute', 'enter', isLoggedIn)
    let currentPage = this.getCurrentPage()
    let currentPageKey = this.getRouteKeyFromLocation(currentPage)
    let isGuardedRoute = this.isStringInList(currentPageKey, Routing.ROUTE_GUARD)

    let isIgnoreGuardRoute = this.isStringInList(currentPageKey, Routing.IGNORE_ROUTE_GUARD)

    if (!isLoggedIn && isGuardedRoute && !isIgnoreGuardRoute) {
      ConsoleLog(
        DEBUG_ROUTING,
        'guardCurrentRoute',
        'not logged in and is guarded and not ignored',
        Routing.ROUTES.login
      )
      let lastPage = this.getLastPage()
      let lastPageKey = this.getRouteKeyFromLocation(lastPage)
      let isLastPageGuarded = this.isStringInList(lastPageKey, Routing.ROUTE_GUARD)
      if (isLastPageGuarded) {
        ConsoleLog(DEBUG_ROUTING, 'guardCurrentRoute', 'last page is guarded', Routing.ROUTES.login)
        this.goTo(Routing.ROUTES[this.getLanguage()].login)
        return
      }

      this.goToLastPage()
    } else {
      ConsoleLog(
        DEBUG_ROUTING,
        'guardCurrentRoute',
        'logged in or not guarded or ignored',
        Routing.HOST
      )
    }
  }

  public getRouteKeyFromCurrentLocation(lang?: string, wildcard: boolean = false): string {
    let currentRoute = this.getCurrentPage()

    return this.getRouteKeyFromLocation(currentRoute, lang, wildcard)
  }

  public getRouteKeyFromLocationLangAgnostic(
    location: string,
    wildcard: boolean = false
  ): { ident: string; lang: string; found: boolean } {
    let loc = Routing.GetLastSlashTrimmedURL(location)
    let ident: string = ''
    let lang: string = ''
    for (let ln in Routing.ROUTES) {
      let routes = Routing.ROUTES[ln]
      for (let k in routes) {
        if (wildcard && location.indexOf(Routing.JoinURLParts(Routing.HOST, routes[k])) === 0) {
          ident = k
          lang = ln
          break
        } else if (location === Routing.JoinURLParts(Routing.HOST, routes[k])) {
          ident = k
          lang = ln
          break
        }
      }
      if (ident) break
    }

    return {
      ident: ident,
      lang: lang,
      found: !!ident,
    }
  }

  public getRouteKeyFromLocation(
    location: string,
    lang?: string,
    wildcard: boolean = false
  ): string {
    let routeIdent = this.getRouteKeyFromLocationLangAgnostic(location, wildcard)
    if (routeIdent.found) {
      return routeIdent.ident
    }
    return ''
  }

  public getLocationListValuesFromRoutes(list: string[], lang?: string): string[] {
    return list.map((val: string) => {
      let path = Routing.ROUTES?.[lang || this.getLanguage()]?.[val] || ''
      if (!path) return path

      return Routing.JoinURLParts(Routing.HOST, path)
    })
  }

  public isStringInList(str: string = '', list: string[]): boolean {
    return list.some((val: string) => {
      if (val && val.length > 0) {
        return str.indexOf(val) === 0
      } else {
        return false
      }
    })
  }

  public getRoute(route: string): string {
    return Routing.JoinURLParts(Routing.HOST, Routing.ROUTES[this.getLanguage()][route])
  }
}

export const getCustomPartnerId = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('customPartnerId')
}
